import 'overlayscrollbars/css/OverlayScrollbars.css';
import '../styles/overlay-scrollbar.nomodule.scss';
import { CLIENT_STATE_KEYS } from '@thoughtspot/client-state-util';
import { getConfig } from '@thoughtspot/custom-style-service';
import { getOnEnter } from '@thoughtspot/radiant-react/widgets/_utils';
import { Button, } from '@thoughtspot/radiant-react/widgets/button/button';
import { ColorPicker } from '@thoughtspot/radiant-react/widgets/colorpicker';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { SwatchShapeOptions } from '@thoughtspot/radiant-react/widgets/selectors';
import { BorderStyle } from '@thoughtspot/radiant-react/widgets/selectors/color/color';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography/typography';
import cx from 'classnames';
import _ from 'lodash';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import React, { useRef } from 'react';
import { isSeriesPresentInVisibleSeries } from '../core/chart-model-utils';
import { configOptions } from '../core/chart-type-specification-service';
import * as pointSelectionService from '../core/point-selection-service/util';
import { getSerie } from '../data/data-util';
import { getEnvFlags } from '../env';
import { BlinkHighchart } from '../highcharts/blink-highchart';
import { getColorOptions, getLegendItems, getLegendWidth, getVisibleSeriesBlinkIds, isLegendLabelPreciselyOverflowing, LEGEND_SCROLL_FACTOR, } from './chart-legend-utils';
const SWATCH_BORDER_COLOR = 'var(--ts-var-viz-legend-swatch-border-color, var(--rd-sys-color-border-bold, #c0c6cf))';
const CLICK_LATENCY_MS = 300;
const SCROLL_DIRECTION = {
    RIGHT: 'right',
    LEFT: 'left',
    TOP: 'top',
    BOTTOM: 'bottom',
};
export const getUpdatedVisibleSeriesNames = (visibleSeriesIds, serieId, isDoubleClick, enabled, allSeriesMap) => {
    const isSeriesVisible = visibleSeriesIds.includes(serieId);
    let updatedVisibleSeriesNames;
    if (isDoubleClick) {
        if (isSeriesVisible) {
            const seriesPreviouslyClicked = visibleSeriesIds.length === 1;
            const seriesToDisplay = seriesPreviouslyClicked
                ? allSeriesMap.map(item => item.blinkSeriesId)
                : [serieId];
            updatedVisibleSeriesNames = [...seriesToDisplay];
        }
        else {
            updatedVisibleSeriesNames = [serieId];
        }
    }
    else if (!isSeriesVisible && enabled) {
        updatedVisibleSeriesNames = [...visibleSeriesIds, serieId];
    }
    else if (isSeriesVisible && !enabled) {
        const updatedVisibleSeriesIds = visibleSeriesIds.filter(value => value !== serieId);
        updatedVisibleSeriesNames = [...updatedVisibleSeriesIds];
    }
    return updatedVisibleSeriesNames;
};
const LegendMarker = ({ color, allowColorChange = true, onChangeColor, isPresented = false, isHighlighted = false, }) => {
    return (React.createElement("div", { className: cx('marker-container-v2', {
            'disable-color-picker': !allowColorChange,
        }) },
        React.createElement("div", { className: "marker-v2", "data-testid": "marker-v2", "aria-hidden": "true" },
            React.createElement(ColorPicker, { colors: getConfig()
                    .getChartColorPalettes()[0]
                    .getColors(), defaultValue: color, swatchShape: SwatchShapeOptions.ROUND, onChange: color1 => onChangeColor(color1), isReferenceVisible: isPresented, tabIndex: -1, border: isHighlighted
                    ? {
                        width: 2,
                        style: BorderStyle.SOLID,
                        color: pointSelectionService.borderColor,
                    }
                    : {
                        width: 1,
                        style: BorderStyle.SOLID,
                        color: SWATCH_BORDER_COLOR,
                    } }))));
};
const LegendItem = ({ legendItem, allowColorChange, onToggle, updateHoverState, isPresented, isHighlighted = false, }) => {
    const clickCount = useRef(0);
    const timerId = useRef(null);
    const { id, color, label, enabled } = legendItem;
    const handleClick = (event) => {
        try {
            if (event.currentTarget instanceof Element &&
                event.target instanceof Element &&
                !event.target.isSameNode(event.currentTarget)) {
                if (event.target.nodeName !== 'P') {
                    return;
                }
            }
            event.persist();
            clickCount.current += 1;
            clearTimeout(timerId.current);
            timerId.current = setTimeout(() => {
                onToggle(id, !enabled, clickCount.current > 1);
                if (clickCount.current === 1)
                    updateHoverState(label, false);
                clickCount.current = 0;
            }, CLICK_LATENCY_MS);
        }
        catch (_a) {
        }
    };
    const handleHover = (event) => {
        if (!enabled)
            return;
        if (event.type === 'mouseenter')
            updateHoverState(label, true);
        else if (event.type === 'mouseleave')
            updateHoverState(label, false);
    };
    return (React.createElement("div", { className: cx('legend-item-v2', {
            disabled: !enabled,
        }), key: id, onKeyDown: getOnEnter(handleClick), "aria-disabled": !enabled, tabIndex: 0, onClick: handleClick, onMouseEnter: handleHover, onMouseLeave: handleHover, role: "button" },
        React.createElement(LegendMarker, { color: color, allowColorChange: allowColorChange, onChangeColor: legendItem.closeCallback, isPresented: isPresented, isHighlighted: isHighlighted }),
        React.createElement("div", { className: "label-v2" },
            React.createElement(Typography, { variant: "p-small", color: isHighlighted ? Colors.accent : Colors.gray, className: "legend-text-v2", ellipsis: { rows: 1 }, noMargin: true, isPreciselyOverflowing: isLegendLabelPreciselyOverflowing }, label))));
};
export const ChartLegend = ({ chartObj, chartModel, dataModel, allowColorChange, onLegendClick, onUpdateColor, getTranslatedText, vizContainerWidth, fontSize, isLegendVertical, isPresented, }) => {
    const [hasVerticalScrollbar, setHasVerticalScrollbar] = React.useState(null);
    const [hasHorizontalScrollbar, setHasHorizontalScrollbar] = React.useState(null);
    const ref = useRef();
    const moveHorizontalScroll = (direction) => {
        let scrollMovement;
        const clientWidth = ref.current.osInstance().getElements().viewport.clientWidth /
            LEGEND_SCROLL_FACTOR;
        const clientLeft = ref.current.osInstance().getElements().viewport
            .scrollLeft;
        if (direction === SCROLL_DIRECTION.LEFT)
            scrollMovement = clientLeft - clientWidth;
        else
            scrollMovement = clientLeft + clientWidth;
        ref.current
            .osInstance()
            .getElements()
            .viewport.scrollTo(scrollMovement, 0);
    };
    const moveVerticalScroll = (direction) => {
        let scrollMovement;
        const clientHeight = ref.current.osInstance().getElements().viewport.clientHeight /
            LEGEND_SCROLL_FACTOR;
        const clientTop = ref.current.osInstance().getElements().viewport
            .scrollTop;
        if (direction === SCROLL_DIRECTION.TOP)
            scrollMovement = clientTop - clientHeight;
        else
            scrollMovement = clientHeight + clientTop;
        ref.current
            .osInstance()
            .getElements()
            .viewport.scrollTo(0, scrollMovement);
    };
    const updateColorHandler = (vizProp, props, val) => {
        const vizModel = chartObj.getVizModel();
        onUpdateColor(vizModel.vizProp, props, val);
        chartObj.updateChartColor(props, val);
    };
    const colorOptions = getColorOptions(chartModel, dataModel, updateColorHandler, getTranslatedText);
    const config = configOptions(chartModel.config.chartType, chartModel);
    const allSeriesMap = !config.doNotShowLegend
        ? colorOptions.map(legendLabel => {
            const serieObj = getSerie(dataModel, legendLabel.caption);
            return {
                blinkSeriesId: serieObj === null || serieObj === void 0 ? void 0 : serieObj.blinkSeriesId.toLowerCase(),
                seriesName: serieObj === null || serieObj === void 0 ? void 0 : serieObj.name.toLowerCase(),
            };
        })
        : [];
    const [visibleSeriesIds, updateVisibleSeriesIds] = React.useState(() => {
        const visibleSeries = isSeriesPresentInVisibleSeries(chartModel, dataModel)
            ? getVisibleSeriesBlinkIds(chartModel, allSeriesMap)
            : [];
        return _.isEmpty(visibleSeries)
            ? allSeriesMap.map(item => item.blinkSeriesId)
            : visibleSeries;
    });
    const legendItems = !config.doNotShowLegend
        ? getLegendItems(colorOptions, chartModel, dataModel, visibleSeriesIds)
        : [];
    const [serieHoverLabel, setSerieHoverLabel] = React.useState(null);
    const [seriesSelectedLabels, setSeriesSelectedLabels] = React.useState([]);
    const updateSerieHoverLabel = (event) => {
        if (!chartObj.chart.container) {
            return;
        }
        if (chartObj.chart.container.id === event.detail.containerId &&
            serieHoverLabel !== event.detail.label) {
            setSerieHoverLabel(event.detail.label);
        }
    };
    const updateSeriesSelectedLabels = (event) => {
        if (!chartObj.chart.container) {
            return;
        }
        if (chartObj.chart.container.id === event.detail.containerId) {
            setSeriesSelectedLabels([...event.detail.labels]);
        }
    };
    const isHighlightedLegend = (label) => {
        return (serieHoverLabel === label || seriesSelectedLabels.includes(label));
    };
    React.useEffect(() => {
        if (getEnvFlags().isFlipTooltipToContextMenuEnabled) {
            document.addEventListener('seriehover', updateSerieHoverLabel);
            document.addEventListener('seriesselect', updateSeriesSelectedLabels);
        }
        return () => {
            if (getEnvFlags().isFlipTooltipToContextMenuEnabled) {
                document.removeEventListener('seriehover', updateSerieHoverLabel);
                document.removeEventListener('seriesselect', updateSeriesSelectedLabels);
            }
        };
    }, []);
    const onToggleItem = (serieId, enabled, isDoubleClick) => {
        const updatedVisibleSeriesIds = getUpdatedVisibleSeriesNames(visibleSeriesIds, serieId, isDoubleClick, enabled, allSeriesMap);
        updateVisibleSeriesIds(updatedVisibleSeriesIds);
        chartObj.updateSeriesVisibility(updatedVisibleSeriesIds);
        const vizModel = chartObj.getVizModel();
        onLegendClick(vizModel.vizProp, [
            CLIENT_STATE_KEYS.chartProperties,
            CLIENT_STATE_KEYS.visibleSeriesNames,
        ], updatedVisibleSeriesIds);
    };
    const updateHoverState = (serieId, isHovered) => {
        if (!(chartObj.chart instanceof BlinkHighchart))
            return;
        const bkChart = chartObj.chart;
        const series = bkChart.getSeries();
        if (!series)
            return;
        if (getEnvFlags().isFlipTooltipToContextMenuEnabled &&
            bkChart.getSelectedPoints().length > 0) {
            pointSelectionService.onLegendHoverState(bkChart, serieId, isHovered);
            return;
        }
        series.forEach(serie => {
            if (serie.name !== serieId) {
                serie.setState(isHovered ? 'inactive' : 'normal');
            }
        });
    };
    return (React.createElement(React.Fragment, null,
        (isLegendVertical
            ? hasVerticalScrollbar
            : hasHorizontalScrollbar) && (React.createElement(React.Fragment, null,
            React.createElement(Button, { "data-testid": "scroll-up-left", icon: isLegendVertical
                    ? IconID.CHEVRON_UP
                    : IconID.CHEVRON_LEFT, size: 'xs', type: 'tertiary', onClick: () => {
                    if (isLegendVertical)
                        moveVerticalScroll(SCROLL_DIRECTION.TOP);
                    else
                        moveHorizontalScroll(SCROLL_DIRECTION.LEFT);
                }, tabIndex: -1 }))),
        React.createElement(OverlayScrollbarsComponent, { style: { overflow: 'hidden', flexGrow: 1, display: 'flex' }, options: {
                scrollbars: {
                    autoHide: 'leave',
                },
                callbacks: {
                    onOverflowChanged: scrollContainer => {
                        if (isLegendVertical)
                            setHasVerticalScrollbar(scrollContainer.yScrollable);
                        else
                            setHasHorizontalScrollbar(scrollContainer.xScrollable);
                    },
                },
            }, ref: ref },
            React.createElement("div", { className: cx('chart-legend-container-v2', {
                    'vertical-legend': isLegendVertical,
                    'horizontal-legend': !isLegendVertical && !hasHorizontalScrollbar,
                }), style: {
                    width: isLegendVertical &&
                        getLegendWidth(vizContainerWidth.toString(), fontSize, dataModel),
                } }, _.map(legendItems, legendItem => (React.createElement(LegendItem, { key: legendItem.id, legendItem: legendItem, onToggle: onToggleItem, updateHoverState: updateHoverState, allowColorChange: allowColorChange, isPresented: isPresented, isHighlighted: isHighlightedLegend(legendItem.label) }))))),
        (isLegendVertical
            ? hasVerticalScrollbar
            : hasHorizontalScrollbar) && (React.createElement(React.Fragment, null,
            React.createElement(Button, { "data-testid": "scroll-down-right", icon: isLegendVertical
                    ? IconID.CHEVRON_DOWN
                    : IconID.CHEVRON_RIGHT, size: "xs", type: "tertiary", onClick: () => {
                    if (isLegendVertical)
                        moveVerticalScroll(SCROLL_DIRECTION.BOTTOM);
                    else
                        moveHorizontalScroll(SCROLL_DIRECTION.RIGHT);
                }, tabIndex: -1 })))));
};
//# sourceMappingURL=chart-legend.js.map